import React from 'react';
import { cn, getScoreColor, getScoreLabel } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Clock } from 'lucide-react';

export function SecurityScoreGauge({ score = 0, trend = 0, lastIncident = null }) {
  const circumference = 2 * Math.PI * 90;
  const progress = (score / 100) * circumference;
  const dashOffset = circumference - progress;

  const scoreColor = getScoreColor(score);
  const scoreLabel = getScoreLabel(score);

  const getStrokeColor = () => {
    if (score >= 90) return '#10b981';
    if (score >= 75) return '#3b82f6';
    if (score >= 60) return '#f59e0b';
    if (score >= 40) return '#f97316';
    return '#ef4444';
  };

  return (
    <div 
      className="flex flex-col items-center justify-center p-6"
      data-testid="security-score-gauge"
    >
      {/* Circular gauge */}
      <div className="relative w-52 h-52">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 200 200">
          {/* Background circle */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke="currentColor"
            strokeWidth="12"
            className="text-secondary"
          />
          {/* Progress circle */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke={getStrokeColor()}
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="transition-all duration-1000 ease-out"
            style={{
              filter: `drop-shadow(0 0 10px ${getStrokeColor()}50)`
            }}
          />
          {/* Glow effect */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke={getStrokeColor()}
            strokeWidth="2"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="blur-sm opacity-50"
          />
        </svg>
        
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-5xl font-mono font-bold", scoreColor)}>
            {score}
          </span>
          <span className={cn("text-sm font-display uppercase tracking-wider mt-1", scoreColor)}>
            {scoreLabel}
          </span>
        </div>
      </div>

      {/* Stats below */}
      <div className="mt-6 flex items-center gap-6">
        {/* Trend */}
        <div className="flex items-center gap-2">
          {trend > 0 ? (
            <TrendingUp className="h-4 w-4 text-success" />
          ) : trend < 0 ? (
            <TrendingDown className="h-4 w-4 text-destructive" />
          ) : (
            <Minus className="h-4 w-4 text-muted-foreground" />
          )}
          <span className={cn(
            "text-sm font-mono",
            trend > 0 ? "text-success" : trend < 0 ? "text-destructive" : "text-muted-foreground"
          )}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-xs text-muted-foreground">vs. semana anterior</span>
        </div>
      </div>

      {/* Last incident */}
      {lastIncident && (
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>Último incidente: {lastIncident}</span>
        </div>
      )}
    </div>
  );
}

export default SecurityScoreGauge;
